import React, { useEffect, useState } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, AlertTriangle, Download, Gift } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LEAD_SOURCES = ['Direct', 'Brokers', 'Partnership', 'Marketing/Inside Sales'];
// Remembered per device so a rep doesn't re-pick their name and centre every visit.
const REMEMBER_REP = 'cw-gift-rep';
const REMEMBER_CENTRE = 'cw-gift-centre';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (s) => (s ? new Date(s.length === 10 ? `${s}T00:00:00` : s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

const readStored = (k) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const writeStored = (k, v) => { try { localStorage.setItem(k, v); } catch { /* private mode */ } };

const emptyForm = () => ({
  sales_name: readStored(REMEMBER_REP),
  centre: readStored(REMEMBER_CENTRE),
  visit_date: todayIso(),
  visit_type: 'Live',
  client_name: '',
  designation: '',
  company: '',
  email: '',
  phone: '',
  visitors: '1',
  seats: '',
  lead_source: 'Direct',
  broker_name: '',
});

// Plain-text payload of the QR code — the front desk scans it for the full details.
const passText = (p) => [
  'CLAYWORKS GIFT PASS',
  `Pass ID: ${p.id}`,
  `Visit date: ${fmtDate(p.visit_date)}`,
  `Centre: ${p.centre}`,
  `Site: ${p.visit_type}`,
  `Sales: ${p.sales_user_name}`,
  `Client: ${p.client_name}${p.designation ? ` (${p.designation})` : ''}`,
  `Company: ${p.company}`,
  `Email: ${p.email}`,
  `Phone: ${p.phone}`,
  `Visitors: ${p.visitors}${p.seats ? ` | Seats: ${p.seats}` : ''}`,
  `Lead source: ${p.lead_source}${p.broker_name ? ` (${p.broker_name})` : ''}`,
  `Tumblers: ${p.tumblers}${p.broker_gift ? ' (incl. 1 for broker)' : ''}`,
  `Requested: ${new Date(p.created_at).toLocaleString('en-IN')}`,
].join('\n');

const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = src; });
const fit = (ctx, text, max) => {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
};

// 1200x640 pass image for WhatsApp/the front desk, same layout as the standalone page.
const passJpg = async (p, qrDataUrl) => {
  const W = 1200, H = 640;
  const c = document.createElement('canvas'); c.width = W; c.height = H;
  const x = c.getContext('2d');
  const g = x.createLinearGradient(0, 0, W, H); g.addColorStop(0, '#E6D8B6'); g.addColorStop(1, '#D4C196');
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  let seed = 0; for (const ch of p.id) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rnd = () => ((seed = (seed * 1664525 + 1013904223) >>> 0) / 4294967296);
  for (let i = 0; i < 1400; i++) {
    x.fillStyle = `rgba(140,116,72,${(0.25 + rnd() * 0.5).toFixed(2)})`;
    x.beginPath(); x.arc(rnd() * W, rnd() * H, 0.6 + rnd() * 1.6, 0, Math.PI * 2); x.fill();
  }
  const ink = '#2A2416', F = 'system-ui, -apple-system, Segoe UI, sans-serif';
  x.fillStyle = ink;
  x.font = `500 26px ${F}`; x.fillText('Clayworks · Gift Pass for the front desk', 64, 92);
  x.font = `700 76px ${F}`; x.fillText(p.id, 64, 182);
  x.font = `600 36px ${F}`; x.fillText(fit(x, p.client_name + (p.designation ? `, ${p.designation}` : ''), 720), 64, 262);
  x.font = `500 32px ${F}`; x.fillText(fit(x, p.company, 720), 64, 310);
  x.font = `400 26px ${F}`;
  [
    `${p.centre} · ${p.visit_type} site`,
    `Visited ${fmtDate(p.visit_date)} · Sales: ${p.sales_user_name}`,
    `Lead source: ${p.lead_source}${p.broker_name ? ` (${p.broker_name})` : ''}`,
    `${p.email} · ${p.phone}`,
  ].forEach((t, i) => x.fillText(fit(x, t, 720), 64, 382 + i * 42));
  x.fillStyle = 'rgba(255,255,255,.6)'; x.fillRect(64, 560, 520, 46);
  x.fillStyle = ink; x.font = `600 22px ${F}`; x.fillText('Show this pass to collect the gift', 88, 591);
  x.strokeStyle = 'rgba(42,36,22,.35)'; x.lineWidth = 3; x.setLineDash([12, 10]);
  x.beginPath(); x.moveTo(840, 30); x.lineTo(840, H - 30); x.stroke(); x.setLineDash([]);
  if (qrDataUrl) {
    const img = await loadImg(qrDataUrl);
    x.fillStyle = '#fff'; x.fillRect(880, 60, 280, 280);
    x.imageSmoothingEnabled = false; x.drawImage(img, 894, 74, 252, 252);
  }
  x.fillStyle = ink; x.textAlign = 'center';
  x.font = `700 110px ${F}`; x.fillText(String(p.tumblers), 1020, 470);
  x.font = `500 26px ${F}`; x.fillText(p.tumblers === 1 ? 'bamboo husk tumbler' : 'bamboo husk tumblers', 1020, 512);
  if (p.broker_gift) { x.font = `400 22px ${F}`; x.fillText('incl. 1 for broker', 1020, 548); }
  return new Promise((res) => c.toBlob(res, 'image/jpeg', 0.92));
};

const downloadBlob = (blob, filename) => {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
};

const PassCard = ({ pass, onNew }) => {
  const [qr, setQr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(passText(pass), { width: 400, margin: 1, errorCorrectionLevel: 'M' }).then((url) => {
      if (!cancelled) setQr(url);
    });
    return () => { cancelled = true; };
  }, [pass]);

  const saveJpg = async () => {
    try {
      const blob = await passJpg(pass, qr);
      if (!blob) throw new Error('no blob');
      downloadBlob(blob, `${pass.id}.jpg`);
    } catch {
      toast.error('Could not create the image');
    }
  };

  return (
    <div className="space-y-4" data-testid="gift-pass-result">
      <Card className="overflow-hidden border-0" style={{ background: 'linear-gradient(135deg,#E6D8B6,#D4C196)', color: '#2A2416' }}>
        <CardContent className="p-0 flex flex-col sm:flex-row">
          <div className="flex-1 p-6 space-y-1.5">
            <div className="text-xs font-medium opacity-80">Gift Pass for the front desk</div>
            <div className="font-heading text-3xl font-bold tracking-tight" data-testid="gift-pass-id">{pass.id}</div>
            <div className="font-semibold">{pass.client_name}, {pass.company}</div>
            <div className="text-sm opacity-80">{pass.centre} · {fmtDate(pass.visit_date)} · {pass.sales_user_name}</div>
            <span className="inline-block mt-2 text-xs font-semibold bg-white/60 rounded-full px-3 py-1">Show this pass to collect the gift</span>
          </div>
          <div className="sm:w-56 p-6 flex flex-col items-center justify-center gap-2 border-t sm:border-t-0 sm:border-l border-dashed border-[#2A2416]/30 text-center">
            {qr ? <img src={qr} alt="QR code with the gift pass details" className="w-36 h-36 bg-white p-1.5 rounded-lg" /> : <div className="w-36 h-36 bg-white/50 rounded-lg animate-pulse" />}
            <div className="text-4xl font-bold leading-none">{pass.tumblers}</div>
            <div className="text-xs">{pass.tumblers === 1 ? 'bamboo husk tumbler' : 'bamboo husk tumblers'}{pass.broker_gift ? ' (incl. 1 for broker)' : ''}</div>
          </div>
        </CardContent>
      </Card>
      {pass.possible_duplicate_of && (
        <p className="text-sm text-amber-600" data-testid="gift-pass-duplicate-note">
          Heads up: {pass.company} already has Gift Pass {pass.possible_duplicate_of} from the last 30 days. Marketing Ops will see both.
        </p>
      )}
      <p className="text-sm text-muted-foreground">Saved. {pass.company} has been added to your Client Database for follow-up.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={saveJpg} disabled={!qr} data-testid="gift-pass-save-jpg"><Download className="w-4 h-4 mr-1.5" /> Save as JPG</Button>
        <Button variant="outline" onClick={onNew} data-testid="gift-pass-new">Request another gift</Button>
      </div>
    </div>
  );
};

const GiftPassPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [salesNames, setSalesNames] = useState([]);
  const [centres, setCentres] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [pass, setPass] = useState(null);
  const [website, setWebsite] = useState(''); // honeypot — left empty by real reps

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/gift-pass/form`)
      .then((res) => {
        if (cancelled) return;
        const names = res.data.sales_names || [];
        setSalesNames(names);
        setCentres(res.data.centres || []);
        // Drop a remembered name that's no longer on the list.
        setForm((f) => (names.includes(f.sales_name) ? f : { ...f, sales_name: '' }));
      })
      .catch(() => { if (!cancelled) setError('Could not load the gift form. Please try again in a moment.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));

  const isBroker = form.lead_source === 'Brokers';

  const validate = () => {
    if (!form.sales_name) return 'Select your name';
    if (!form.centre.trim()) return 'Enter the centre';
    if (!form.visit_date) return 'Pick the visit date';
    if (!form.client_name.trim()) return 'Enter the client name';
    if (!form.company.trim()) return 'Enter the company';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'Check the email address';
    if (form.phone.replace(/\D/g, '').length < 10) return 'Phone needs at least 10 digits';
    const v = parseInt(form.visitors, 10); if (!(v >= 1 && v <= 20)) return 'People on the visit must be between 1 and 20';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/gift-pass/submit`, {
        ...form,
        centre: form.centre.trim(),
        visitors: parseInt(form.visitors, 10),
        seats: form.seats ? parseInt(form.seats, 10) : null,
        broker_name: isBroker ? form.broker_name.trim() : null,
        website,
      });
      writeStored(REMEMBER_REP, form.sales_name);
      writeStored(REMEMBER_CENTRE, form.centre.trim());
      setPass(res.data.pass);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save the request. Try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  const startNew = () => { setPass(null); setForm(emptyForm()); };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="gift-pass-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
            <h2 className="text-xl font-heading">Cannot open the gift form</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const sectionTitle = 'text-sm font-semibold uppercase tracking-wider text-muted-foreground';

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="gift-pass-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo.webp" alt="Clayworks" className="h-10 w-auto mx-auto" />
          <p className="text-xs text-muted-foreground tracking-widest mt-2">POST-VISIT GIFT</p>
        </div>

        {pass ? (
          <PassCard pass={pass} onNew={startNew} />
        ) : (
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} noValidate className="space-y-8">
                <div>
                  <h2 className="text-2xl font-heading mb-1 flex items-center gap-2"><Gift className="w-6 h-6 text-primary" /> Request a gift</h2>
                  <p className="text-sm text-muted-foreground">Log the site visit, get a Gift Pass, and collect the bamboo husk tumbler for your client.</p>
                </div>

                {/* Honeypot field — hidden from real reps, bots tend to fill every field */}
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  className="absolute left-[-9999px] w-px h-px opacity-0"
                  aria-hidden="true"
                />

                <section className="space-y-4">
                  <h3 className={sectionTitle}>The visit</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Your name *</Label>
                      <Select value={form.sales_name} onValueChange={set('sales_name')}>
                        <SelectTrigger data-testid="gift-pass-sales-select"><SelectValue placeholder="Select your name" /></SelectTrigger>
                        <SelectContent>
                          {salesNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-centre">Centre *</Label>
                      <Input id="gp-centre" list="gp-centres" value={form.centre} onChange={set('centre')} placeholder="e.g. Clayworks Create Campus" data-testid="gift-pass-centre-input" />
                      <datalist id="gp-centres">{centres.map((c) => <option key={c} value={c} />)}</datalist>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-date">Visit date *</Label>
                      <Input id="gp-date" type="date" value={form.visit_date} onChange={set('visit_date')} />
                    </div>
                    <div className="space-y-2">
                      <Label>Site status</Label>
                      <div className="flex gap-2">
                        {[['Live', 'Live centre'], ['WIP', 'WIP site']].map(([v, label]) => (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setForm((f) => ({ ...f, visit_type: v }))}
                            className={`flex-1 h-10 rounded-md border text-sm transition-colors ${form.visit_type === v ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted/50'}`}
                            aria-pressed={form.visit_type === v}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className={sectionTitle}>The client</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gp-client">Client name *</Label>
                      <Input id="gp-client" value={form.client_name} onChange={set('client_name')} data-testid="gift-pass-client-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-designation">Designation <span className="text-muted-foreground font-normal">(optional)</span></Label>
                      <Input id="gp-designation" value={form.designation} onChange={set('designation')} />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="gp-company">Company *</Label>
                      <Input id="gp-company" value={form.company} onChange={set('company')} data-testid="gift-pass-company-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-email">Email *</Label>
                      <Input id="gp-email" type="email" inputMode="email" value={form.email} onChange={set('email')} data-testid="gift-pass-email-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-phone">Phone *</Label>
                      <Input id="gp-phone" type="tel" inputMode="tel" value={form.phone} onChange={set('phone')} data-testid="gift-pass-phone-input" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-visitors">People on the visit *</Label>
                      <Input id="gp-visitors" type="number" min="1" max="20" value={form.visitors} onChange={set('visitors')} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gp-seats">Seats required <span className="text-muted-foreground font-normal">(optional)</span></Label>
                      <Input id="gp-seats" type="number" min="1" value={form.seats} onChange={set('seats')} />
                    </div>
                    <div className="space-y-2">
                      <Label>Lead source</Label>
                      <Select value={form.lead_source} onValueChange={set('lead_source')}>
                        <SelectTrigger data-testid="gift-pass-source-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {LEAD_SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {isBroker && (
                      <div className="space-y-2">
                        <Label htmlFor="gp-broker">Broker / firm</Label>
                        <Input id="gp-broker" value={form.broker_name} onChange={set('broker_name')} />
                      </div>
                    )}
                  </div>
                </section>

                <Button type="submit" disabled={submitting} className="w-full" size="lg" data-testid="gift-pass-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Get Gift Pass
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-muted-foreground mt-6">
          Clayworks Spaces Technologies Pvt Ltd
        </p>
      </div>
    </div>
  );
};

export default GiftPassPage;
